/**
 * custom-tracking.js
 * GA4 / dataLayer tracking for add-to-cart events.
 *
 * This code used to live inside product-form.js and personalisation-box.js,
 * in the middle of the /cart/add fetch chain. Any exception it threw broke
 * the add-to-cart flow silently. It now runs as a subscriber to the theme's
 * pubsub cart events, fully isolated: a tracking failure can never affect
 * the shopper's cart.
 *
 * Depends on: pubsub.js (subscribe), constants.js (PUB_SUB_EVENTS).
 * Optional: saveItemToLocalStorage / getListNameById from global.js.
 */
(function () {
  'use strict';

  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  function getDataLayer() {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  function platform() {
    return document.documentElement.clientWidth > 767 ? 'd-web' : 'm-web';
  }

  function optionValue(options, names) {
    if (!Array.isArray(options)) return 'NA';
    const found = options.find(
      (o) => o && o.name && names.includes(o.name.trim().toLowerCase())
    );
    return (found && found.value) || 'NA';
  }

  function listNameFor(productId, formElement) {
    try {
      const mainSection =
        formElement &&
        (formElement.closest('.shopify-section') || formElement.closest('quick-add-modal'));
      const listNameText = mainSection
        ? mainSection
            .querySelector('.Common_small_title_new, h1, h2, h3, h4, h5, h6')
            ?.innerText?.trim()
        : '';
      const listName = location.href.includes('/collections')
        ? document.querySelector('h1')?.innerText?.trim() || 'NA'
        : listNameText || '';
      if (typeof saveItemToLocalStorage === 'function' && productId) {
        saveItemToLocalStorage(productId, listName);
      }
      if (typeof getListNameById === 'function' && productId) {
        return getListNameById(productId);
      }
      return listName || 'NA';
    } catch (e) {
      return 'NA';
    }
  }

  function mainProductPrice(formElement) {
    let price = NaN;
    let comparePrice = 0;
    if (formElement && formElement.getAttribute('is') === 'main-product') {
      const priceEl = document.querySelector('[js-product-price]');
      const compareEl = document.querySelector('[js-product-compare-price]');
      if (priceEl) price = parseFloat(priceEl.getAttribute('data-price'));
      if (compareEl) comparePrice = parseFloat(compareEl.getAttribute('data-discount-price')) || 0;
    } else if (formElement) {
      price = parseFloat(formElement.getAttribute('data-price'));
      comparePrice = parseFloat(formElement.getAttribute('data-discount-price')) || 0;
    }
    return { price: price, comparePrice: comparePrice };
  }

  function itemFromLine(line, index, formElement, overrides) {
    const productId = line.product_id || line.id;
    return Object.assign(
      {
        item_id: productId,
        item_name: line.product_title || line.title || 'NA',
        discount: (line.total_discount || 0) / 100,
        index: index,
        item_brand: line.vendor || 'NA',
        item_category:
          line.product_type || (formElement && formElement.getAttribute('data-category')) || 'NA',
        item_category2: optionValue(line.options_with_values, ['colour', 'color']),
        item_category3: optionValue(line.options_with_values, ['material']),
        item_variant: optionValue(line.options_with_values, ['size']),
        item_height: optionValue(line.options_with_values, ['height']),
        item_list_name: listNameFor(productId, formElement),
        item_list_id: (formElement && formElement.getAttribute('data-section-id')) || 'NA',
        price: (line.final_price || line.price || 0) / 100,
        quantity: line.quantity || 1,
      },
      overrides || {}
    );
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
    try {
      if (!event || !event.cartData) return;
      // Only track events that come from an add-to-cart action.
      if (event.source !== 'product-form' && event.source !== 'personalisation-box') return;

      const response = event.cartData;
      const formElement = event.formElement || null;
      const currency =
        (formElement && formElement.getAttribute('data-currency')) ||
        (window.Shopify && Shopify.currency && Shopify.currency.active) ||
        'GBP';
      const index =
        formElement && formElement.dataset && formElement.dataset.index
          ? +formElement.dataset.index || 0
          : 0;

      let items = [];
      let value = 0;

      if (Array.isArray(response.items)) {
        // Multi-item add (items[] payload, e.g. personalised letters).
        items = response.items.map(function (line, i) {
          return itemFromLine(line, index || i, formElement);
        });
        value = response.items.reduce(function (total, line) {
          return total + ((line.final_price || line.price || 0) * (line.quantity || 1)) / 100;
        }, 0);
      } else if (response.product_id || response.id) {
        // Single-item add (standard product form).
        const qty = event.quantity || response.quantity || 1;
        const domPrice = mainProductPrice(formElement);
        const linePrice = isNaN(domPrice.price)
          ? (response.final_price || response.price || 0) / 100
          : domPrice.price;
        items = [
          itemFromLine(response, index, formElement, {
            discount: domPrice.comparePrice || (response.total_discount || 0) / 100,
            price: linePrice,
            quantity: qty,
          }),
        ];
        value = linePrice * qty;
      } else {
        return;
      }

      getDataLayer().push({
        event: 'add_to_cart_ce',
        platform: platform(),
        ecommerce: {
          currency: currency,
          value: value,
          items: items,
        },
      });
    } catch (e) {
      // Tracking must never break the cart. Log and move on.
      console.warn('[custom-tracking] add_to_cart_ce skipped:', e);
    }
  });
})();
